import { z } from "zod";

import {
  cableLinksSchema,
  calculationRunListResponseSchema,
  disconnectionLinksSchema,
  earthFaultLinksSchema,
  faultLinkSchema,
  loadLinkSchema,
  sldLinkSchema,
} from "./calculationRun";
import { ApiError, describeApiFieldError, requestJson } from "./http";

// The printable report of one stored run (EOS-13, Master Prompt A31). Every schema here mirrors
// backend/app/schemas/report.py field by field, strictly: a field the server sends that is not
// listed fails the parse, and nothing is optional that the server always sends. The browser
// never computes or alters a checksum - `report_checksum` and `content_hash` are shown as
// received - and never judges the evidence: the backend's REVIEW_REQUIRED report warnings pass
// through unchanged. reportContract.test.ts holds this file against report.fixture.json, a real
// backend response kept honest by backend/tests/api/test_report_fixture.py.

const REPORTS_URL = "/api/v1/electrical/reports/runs";

const UNEXPECTED = "Unexpected response from the KES Electrical OS reports API.";

const sha256Schema = z.string().regex(/^[0-9a-f]{64}$/);

const snapshotSchema = z.record(z.string(), z.unknown());

// The project block of the sheet; every field but the profile is null for an unassigned run.
export const reportHeaderSchema = z
  .object({
    organization_name: z.string().nullable(),
    site_code: z.string().nullable(),
    site_name: z.string().nullable(),
    project_code: z.string().nullable(),
    project_name: z.string().nullable(),
    client_name: z.string().nullable(),
    revision_number: z.number().int().nullable(),
    revision_label: z.string().nullable(),
    revision_status: z.string().nullable(),
    jurisdiction_profile: z.string(),
  })
  .strict();

export const reportModuleSchema = z
  .object({
    module_code: z.string(),
    module_name: z.string(),
    calculation_type: z.string(),
    calculation_key: z.string(),
    revision_number: z.number().int(),
  })
  .strict();

// Which rule picked the run: named by id, the study's approved run, or its latest revision.
export const reportSelectionRuleSchema = z.enum(["RUN_ID", "APPROVED", "LATEST"]);

export const reportSelectionSchema = z
  .object({
    rule: reportSelectionRuleSchema,
    latest_revision_number: z.number().int(),
    later_revision_exists: z.boolean(),
    later_revision_approved: z.boolean(),
  })
  .strict();

// `is_draft` is true unless the run is APPROVED: the sheet then carries the DRAFT watermark.
export const reportStatusSchema = z
  .object({
    design_check_status: z.string(),
    reference_verification_status: z.string(),
    run_status: z.string(),
    approval_status: z.string(),
    is_immutable: z.boolean(),
    is_draft: z.boolean(),
  })
  .strict();

export const reportSignaturesSchema = z
  .object({
    prepared_by: z.string().nullable(),
    prepared_at: z.string(),
    submitted_by: z.string().nullable(),
    submitted_at: z.string().nullable(),
    approved_by: z.string().nullable(),
    approved_at: z.string().nullable(),
    approval_notes: z.string().nullable(),
    rejected_by: z.string().nullable(),
    rejected_at: z.string().nullable(),
    rejection_reason: z.string().nullable(),
    supersedes_run_id: z.string().uuid().nullable(),
    // Render-only, derived by the server at build time (D7, revised): the approver's name in the
    // "Name (email)" form of prepared_by, and whether the preparer's user id is the approver's
    // (true), another user's (false) or not established (null). Never in the checksum.
    approved_by_name: z.string().nullable(),
    self_checked: z.boolean().nullable(),
  })
  .strict();

// A value the stored run does not carry, named on the sheet instead of invented; never evidence.
export const reportWarningStatusSchema = z.enum(["REVIEW_REQUIRED"]);

export const reportWarningSchema = z
  .object({
    code: z.string(),
    message: z.string(),
    status: reportWarningStatusSchema,
  })
  .strict();

export const reportDocumentSchema = z
  .object({
    run_id: z.string().uuid(),
    generated_at: z.string(),
    header: reportHeaderSchema,
    module: reportModuleSchema,
    selection: reportSelectionSchema,
    status: reportStatusSchema,
    signatures: reportSignaturesSchema,
    engine_version: z.string(),
    // The hash stored with the run, and the same rule recomputed by the server from the
    // snapshots it serves; both shown as received.
    content_hash: sha256Schema,
    report_checksum: sha256Schema,
    input_snapshot: snapshotSchema,
    result_snapshot: snapshotSchema,
    warnings_snapshot: z.array(snapshotSchema),
    references_snapshot: snapshotSchema,
    sld_link: sldLinkSchema.nullable(),
    fault_link: faultLinkSchema.nullable(),
    // The load run a power quality study read its demand figures from (EOS-11, A32); null
    // for a run without one, absent from a document a server before EOS-11 served (stored
    // evidence such as the legacy busbar fixture keeps its own shape).
    load_link: loadLinkSchema.nullable().optional(),
    // The cable runs the rows of an earth-fault loop study read their figures from, keyed by
    // the row code (EOS-05d, A33); null for a run without one, absent from a document a server
    // before EOS-05d served.
    cable_links: cableLinksSchema.nullable().optional(),
    // The earth-fault runs the board conductors of an earthing study read their current from and
    // the earth-fault loop rows its circuit protective conductors read their figures from, each
    // keyed by the conductor row code (EOS-08a, A36); null for a run without one, absent from a
    // document a server before EOS-08a served.
    fault_links: earthFaultLinksSchema.nullable().optional(),
    disconnection_links: disconnectionLinksSchema.nullable().optional(),
    notes: z.string().nullable(),
    report_warnings: z.array(reportWarningSchema),
    // The server's fixed notes on the basis of the result (A38 (d): the IR-009 note on every
    // fault study), shown as received; never evidence. Absent from a document a server before
    // A38 served.
    evidence_notes: z.array(z.string().min(1)).optional(),
  })
  .strict();

export type ReportHeader = z.infer<typeof reportHeaderSchema>;
export type ReportModule = z.infer<typeof reportModuleSchema>;
export type ReportSelection = z.infer<typeof reportSelectionSchema>;
export type ReportSelectionRule = z.infer<typeof reportSelectionRuleSchema>;
export type ReportStatus = z.infer<typeof reportStatusSchema>;
export type ReportSignatures = z.infer<typeof reportSignaturesSchema>;
export type ReportWarning = z.infer<typeof reportWarningSchema>;
export type ReportDocument = z.infer<typeof reportDocumentSchema>;

// One page of the stored runs of one scope across modules, newest first
// (ReportRunListResponse, F2): the same run summaries every study page lists, the opaque
// cursor of the page after this one (null on the last page) and the size of the whole
// filtered set.
export const reportRunListResponseSchema = z
  .object({
    items: calculationRunListResponseSchema.shape.items,
    next_cursor: z.string().min(1).nullable(),
    total_count: z.number().int().nonnegative(),
  })
  .strict();
export type ReportRunListResponse = z.infer<typeof reportRunListResponseSchema>;

export const RUN_STATUSES = ["DRAFT", "COMPLETED", "FAILED"] as const;
export const APPROVAL_STATUSES = ["NOT_SUBMITTED", "PENDING", "APPROVED", "REJECTED"] as const;
export type RunStatusFilter = (typeof RUN_STATUSES)[number];
export type ApprovalStatusFilter = (typeof APPROVAL_STATUSES)[number];

// The two request bodies of the approval action (OWNER only on the server).
export const runApprovalRequestSchema = z
  .object({ notes: z.string().trim().min(1).max(2000).optional() })
  .strict();

export const runRejectionRequestSchema = z
  .object({ reason: z.string().trim().min(1).max(2000) })
  .strict();

export type RunApprovalRequest = z.infer<typeof runApprovalRequestSchema>;
export type RunRejectionRequest = z.infer<typeof runRejectionRequestSchema>;

// -- errors -------------------------------------------------------------------------------------

// The server's conflict codes (backend/app/services/run_approval.py) plus the client's own.
export type ReportErrorCode =
  | "UNAUTHENTICATED"
  | "FORBIDDEN"
  | "NOT_FOUND"
  | "VALIDATION"
  | "CONFLICT"
  | "ALREADY_APPROVED"
  | "ALREADY_REJECTED"
  | "REVISION_ISSUED"
  | "EVIDENCE_CHECKSUM_MISMATCH"
  | "REASON_REQUIRED"
  | "UNEXPECTED_STATUS";

const CONFLICT_CODES = new Set<ReportErrorCode>([
  "ALREADY_APPROVED",
  "ALREADY_REJECTED",
  "REVISION_ISSUED",
  "EVIDENCE_CHECKSUM_MISMATCH",
]);

/** An ApiError that also carries the error code, so a page can act on it without parsing text. */
export class ReportApiError extends ApiError {
  readonly code: ReportErrorCode;

  constructor(message: string, status: number, code: ReportErrorCode) {
    super(message, status);
    this.name = "ReportApiError";
    this.code = code;
  }
}

// A 409 of the approval action carries {"code", "message"}; the other refusals a plain string
// or the field list the shared helper reads.
const conflictDetailSchema = z.object({
  detail: z.object({ code: z.string(), message: z.string() }).passthrough(),
});

function refusal(data: unknown, status: number, fallback: string): ReportApiError {
  const conflict = conflictDetailSchema.safeParse(data);
  if (status === 409 && conflict.success) {
    const code = conflict.data.detail.code;
    return new ReportApiError(
      conflict.data.detail.message,
      status,
      CONFLICT_CODES.has(code as ReportErrorCode) ? (code as ReportErrorCode) : "CONFLICT",
    );
  }

  const message = describeApiFieldError(data, status, fallback);
  switch (status) {
    case 401:
      return new ReportApiError(message, status, "UNAUTHENTICATED");
    case 403:
      return new ReportApiError(message, status, "FORBIDDEN");
    case 404:
      return new ReportApiError(message, status, "NOT_FOUND");
    case 409:
      return new ReportApiError(message, status, "CONFLICT");
    case 422:
      return new ReportApiError(message, status, "VALIDATION");
    default:
      return new ReportApiError(message, status, "UNEXPECTED_STATUS");
  }
}

function parse<T>(schema: z.ZodType<T>, data: unknown): T {
  const parsed = schema.safeParse(data);

  if (!parsed.success) {
    throw new Error(UNEXPECTED);
  }

  return parsed.data;
}

// -- reads ----------------------------------------------------------------------------------------

export type ReportKey = {
  module_code: string;
  calculation_type: string;
  calculation_key: string;
  project_revision_id?: string;
};

export type ReportRunFilter = {
  project_revision_id?: string;
  module_code?: string;
  /** The exact study key. */
  calculation_key?: string;
  /** A case-insensitive prefix of the study key (the landing page's search box). */
  calculation_key_prefix?: string;
  run_status?: RunStatusFilter;
  approval_status?: ApprovalStatusFilter;
  limit?: number;
  /** The `next_cursor` of the page before; left out for the first page. */
  cursor?: string;
};

/** The report of one stored run by id (selection rule RUN_ID). */
export async function getReport(runId: string, signal?: AbortSignal): Promise<ReportDocument> {
  const response = await requestJson(`${REPORTS_URL}/${encodeURIComponent(runId)}`, {
    method: "GET",
    signal,
  });

  if (!response.ok) {
    throw refusal(response.data, response.status, "The report could not be loaded");
  }

  return parse(reportDocumentSchema, response.data);
}

/**
 * The report of a study by key: its latest approved revision, else its latest revision as a
 * draft. The server decides; the browser only asks.
 */
export async function buildReport(key: ReportKey, signal?: AbortSignal): Promise<ReportDocument> {
  const query = new URLSearchParams();
  query.set("module_code", key.module_code);
  query.set("calculation_type", key.calculation_type);
  query.set("calculation_key", key.calculation_key);
  if (key.project_revision_id !== undefined) {
    query.set("project_revision_id", key.project_revision_id);
  }

  const response = await requestJson(`${REPORTS_URL}/by-key?${query.toString()}`, {
    method: "GET",
    signal,
  });

  if (!response.ok) {
    throw refusal(response.data, response.status, "The report could not be built");
  }

  return parse(reportDocumentSchema, response.data);
}

/** One page of the stored runs of one scope, newest first; the filters narrow, the cursor pages. */
export async function listReports(
  filter: ReportRunFilter = {},
  signal?: AbortSignal,
): Promise<ReportRunListResponse> {
  const query = new URLSearchParams();

  if (filter.project_revision_id !== undefined) {
    query.set("project_revision_id", filter.project_revision_id);
  }
  if (filter.module_code !== undefined) {
    query.set("module_code", filter.module_code);
  }
  if (filter.calculation_key !== undefined) {
    query.set("calculation_key", filter.calculation_key);
  }
  if (filter.calculation_key_prefix !== undefined) {
    query.set("calculation_key_prefix", filter.calculation_key_prefix);
  }
  if (filter.run_status !== undefined) {
    query.set("run_status", filter.run_status);
  }
  if (filter.approval_status !== undefined) {
    query.set("approval_status", filter.approval_status);
  }
  if (filter.limit !== undefined) {
    query.set("limit", String(filter.limit));
  }
  if (filter.cursor !== undefined) {
    query.set("cursor", filter.cursor);
  }

  const text = query.toString();
  const response = await requestJson(text === "" ? REPORTS_URL : `${REPORTS_URL}?${text}`, {
    method: "GET",
    signal,
  });

  if (!response.ok) {
    throw refusal(response.data, response.status, "The reports could not be listed");
  }

  return parse(reportRunListResponseSchema, response.data);
}

// -- the approval action (OWNER only; the server checks the role and the evidence) ------------------

/** Approve a stored run with optional notes; the server returns the approved report. */
export async function approveRun(
  runId: string,
  notes?: string,
  signal?: AbortSignal,
): Promise<ReportDocument> {
  const trimmed = notes?.trim();
  const body: RunApprovalRequest = trimmed ? { notes: trimmed } : {};
  const response = await requestJson(`${REPORTS_URL}/${encodeURIComponent(runId)}/approve`, {
    method: "POST",
    body: runApprovalRequestSchema.parse(body),
    signal,
  });

  if (!response.ok) {
    throw refusal(response.data, response.status, "The report action failed");
  }

  return parse(reportDocumentSchema, response.data);
}

/** Reject a stored run with a reason; a blank reason is refused here, before the request. */
export async function rejectRun(
  runId: string,
  reason: string,
  signal?: AbortSignal,
): Promise<ReportDocument> {
  const trimmed = reason.trim();
  if (trimmed === "") {
    throw new ReportApiError("A reason is required to reject a run.", 422, "REASON_REQUIRED");
  }

  const response = await requestJson(`${REPORTS_URL}/${encodeURIComponent(runId)}/reject`, {
    method: "POST",
    body: runRejectionRequestSchema.parse({ reason: trimmed }),
    signal,
  });

  if (!response.ok) {
    throw refusal(response.data, response.status, "The report action failed");
  }

  return parse(reportDocumentSchema, response.data);
}
